import { useState, useCallback } from 'react';

export type LookupCache = Record<string, Record<string, any>>;

export interface UseLookupCacheReturn {
  cache: LookupCache;
  getCachedData: (schemaKey: string, itemId: string) => any | null;
  setCachedData: (schemaKey: string, itemId: string, data: any) => void;
  isCached: (schemaKey: string, itemId: string) => boolean;
  clearCache: () => void;
  clearSchemaCache: (schemaKey: string) => void;
}

/**
 * Hook to manage lookup data caching
 * Prevents re-fetching related collection data repeatedly
 * Used for x-lookup fields that reference other collections
 */
export const useLookupCache = (): UseLookupCacheReturn => {
  const [cache, setCache] = useState<LookupCache>({});

  const getCachedData = useCallback(
    (schemaKey: string, itemId: string): any | null => {
      return cache[schemaKey]?.[itemId] || null;
    },
    [cache]
  );

  const setCachedData = useCallback(
    (schemaKey: string, itemId: string, data: any) => {
      setCache(prev => ({
        ...prev,
        [schemaKey]: {
          ...(prev[schemaKey] || {}),
          [itemId]: data,
        },
      }));
    },
    []
  );

  const isCached = useCallback(
    (schemaKey: string, itemId: string): boolean => {
      return getCachedData(schemaKey, itemId) !== null;
    },
    [getCachedData]
  );

  const clearCache = useCallback(() => {
    setCache({});
  }, []);

  const clearSchemaCache = useCallback((schemaKey: string) => {
    setCache(prev => {
      const updated = { ...prev };
      delete updated[schemaKey];
      return updated;
    });
  }, []);

  return {
    cache,
    getCachedData,
    setCachedData,
    isCached,
    clearCache,
    clearSchemaCache,
  };
};

export default useLookupCache;
